import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useAnalyticsPageviews } from './hooks/useAnalytics';
import Home from './pages/Home';
import HomeAlt from './pages/HomeAlt';
import About from './pages/About';
import SendRequest from './pages/SendRequest';
import Checkout from './pages/Checkout';
import Cart from './pages/Cart';

import NotFound from './pages/404';
import Carpenter from './pages/Carpenter';
import GiftCard from './pages/GiftCard';
import Gallery from './pages/Gallery';
import Login from './pages/Login';
import Success from './pages/Success';
import Duk from './pages/Duk';
import Text from './pages/Text';
import FurnitureList from './pages/FurnitureList';
import FurnitureItem from './pages/FurnitureItem';
import Cooperate from './pages/Cooperate';
import PlateComparison from './pages/PlateComparison';
import ResetPassword from './pages/ResetPassword';
import Ekspozicija from './pages/Ekspozicija';

import PrivateRoute from './components/PrivateRoute'; // a wrapper component for auth
import { AuthProvider } from './context/AuthContext';

import Header from './layout/Header';
import Footer from './layout/Footer';

// Fluent 2 — vienintelė UI tema visame projekte. `@mui/material` visiškai
// pašalintas (paskutinis naudotojas — SummaryView.jsx — konvertuotas į Fluent2,
// žr. 20 punktų sąrašo #8) ir išimtas iš package.json.
import { FluentProvider, makeStyles, tokens } from '@fluentui/react-components';
import { fititLightTheme } from './theme/brand';
import ComingSoon from './components/ComingSoon';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// 3D configurator and every admin/user (auth-gated) page are lazy-loaded —
// anonymous shop visitors shouldn't pay for their bundle weight upfront.
const Layout = lazy(() => import('./configurator/Layout'));
// BugTracker pulls in html2canvas + react-draft-wysiwyg + draft-js (heavy) — lazy-
// loaded so it doesn't add to the initial bundle for every visitor; the floating
// FAB simply pops in a moment after the rest of the page (Suspense fallback=null).
// Visible/usable to EVERYONE (anonymous visitors included, not just admin/sysadmin
// — see /admin/bugs "reikia galimybes suregistruoti klaidas is vartotojo puses").
const BugTracker = lazy(() => import('./components/BugTracker'));

const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const UsersPage = lazy(() => import('./admin/UsersPage'));
const AdminUserDetail = lazy(() => import('./admin/AdminUserDetail'));
const TextPages = lazy(() => import('./admin/TextPages'));
const TextPageDetail = lazy(() => import('./admin/TextPageDetail'));
const AdminFurniture = lazy(() => import('./admin/AdminFurniture'));
const CarpenterList = lazy(() => import('./admin/CarpenterList'));
const OrderList = lazy(() => import('./admin/OrderList'));

const AdminPanels = lazy(() => import('./admin/AdminPanels'));
const AdminHandles = lazy(() => import('./admin/AdminHandles'));
const AdminLegs = lazy(() => import('./admin/AdminLegs'));
const AdminMirrors = lazy(() => import('./admin/AdminMirrors'));
const AdminFillings = lazy(() => import('./admin/AdminFillings'));
const AdminMdf = lazy(() => import('./admin/AdminMdf'));
const AdminViewOrder = lazy(() => import('./admin/AdminViewOrder'));

const AdminLanguages = lazy(() => import('./admin/AdminLanguages'));
const AdminDuk = lazy(() => import('./admin/AdminDuk'));
const AdminDiscountCodes = lazy(() => import('./admin/AdminDiscountCodes'));
const AdminGiftCards = lazy(() => import('./admin/AdminGiftCards'));
const AdminSeo = lazy(() => import('./admin/AdminSeo'));
const AdminOffers = lazy(() => import('./admin/AdminOffers.jsx'));
const AdminGallery = lazy(() => import('./admin/AdminGallery'));
const AdminRequests = lazy(() => import('./admin/AdminRequests'));
const Dashboard = lazy(() => import('./admin/Dashboard'));
const AdminAuditLog = lazy(() => import('./admin/AdminAuditLog'));
const AdminAnalytics = lazy(() => import('./admin/AdminAnalytics'));
const AdminBackups = lazy(() => import('./admin/AdminBackups'));
const AdminBugs = lazy(() => import('./admin/AdminBugs'));
const AdminSettings = lazy(() => import('./admin/AdminSettings'));
const AdminImosAssembly = lazy(() => import('./admin/AdminImosAssembly'));
const AdminClickUp = lazy(() => import('./admin/AdminClickUp'));
const AdminNotFound = lazy(() => import('./admin/AdminNotFound'));

const UserLayout = lazy(() => import('./user/UserLayout'));
const UserDashboard = lazy(() => import('./user/UserDashboard'));
const UserFurniture = lazy(() => import('./user/UserFurniture'));
const UserOrders = lazy(() => import('./user/UserOrders'));
const UserProfile = lazy(() => import('./user/UserProfile'));

const useAppStyles = makeStyles({
  fallback: { minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner: {
    width: 40, height: 40, borderRadius: tokens.borderRadiusCircular,
    border: `3px solid ${tokens.colorNeutralStroke2}`, borderTopColor: tokens.colorBrandBackground,
    animationName: { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
    animationDuration: '0.8s', animationIterationCount: 'infinite', animationTimingFunction: 'linear',
  },
});

const RouteFallback = () => {
  const s = useAppStyles();
  return (
    <div className={s.fallback}>
      <div className={s.spinner} />
    </div>
  );
};

// The admin workspace (/admin/*) is a standalone app shell (AdminLayout has its
// own header/nav/back-to-site link) — the public site's Header/Footer/mobile nav
// don't belong there and previously just sat above/below it unstyled. This lives
// inside <Router> (needs useLocation) so it wraps everything between Router and
// the route table.
function AppShell({ children }) {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith('/admin');
    // Lankytojų elgsenos sekimas (puslapių peržiūros + trukmė + paspaudimai) —
    // veikia VISUR (įsk. /admin), visiems lankytojams, ne tik prisijungusiems
    // (žr. /admin/statistika, src/hooks/useAnalytics.js).
    useAnalyticsPageviews();
    return (
        <>
            <ScrollToTop />
            {!isAdmin && <Header />}
            {children}
            {!isAdmin && <Footer />}
            {/* Site-wide (įsk. /admin) — matomas visiems, ne tik admin/sysadmin. */}
            <Suspense fallback={null}>
                <BugTracker />
            </Suspense>
        </>
    );
}

function App() {
    return (
        <ComingSoon>
        <FluentProvider theme={fititLightTheme} style={{ background: 'transparent' }}>
            <AuthProvider>
                <Router>
                    <AppShell>
                    <ErrorBoundary>
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>

                        {/* Public pages */}

                        <Route path="/" element={<Home />} />
                        <Route path="/pradzia-v2" element={<HomeAlt />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/siusti-uzklausa" element={<SendRequest />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/konfiguratorius" element={<Layout />} />
                        <Route path="/konfiguratorius/:code" element={<Layout />} />

                        <Route path="/duk" element={<Duk />} />
                        <Route path="/bendradarbiavimas" element={<Cooperate />} />
                        <Route path="/success/:orderNumber" element={<Success />} />


                        <Route path="/galerija" element={<Gallery />} />
                        <Route path="/ekspozicija" element={<Ekspozicija />} />
                        <Route path="/kontaktai" element={<Ekspozicija />} />
                        <Route path="/meistro-uzsakymas" element={<Carpenter />} />
                        <Route path="/dovanu-cekis" element={<GiftCard />} />

                        <Route path="/apie-mus" element={<About />} />
                        <Route path="/baldai" element={<FurnitureList />} />
                        <Route path="/baldai/spintos-varstomomis-durimis" element={<FurnitureList />} />
                        <Route path="/baldai/spintos-stumdomomis-durimis" element={<FurnitureList />} />
                        <Route path="/baldai/lentynos" element={<FurnitureList />} />
                        <Route path="/baldai/spinteles" element={<FurnitureList />} />
                        <Route path="/baldai/komodos" element={<FurnitureList />} />
                        <Route path="/baldai/tv-spinteles" element={<FurnitureList />} />
                        <Route path="/baldai/:code" element={<FurnitureItem />} />
                        <Route path="/issirinkt-plokstes" element={<PlateComparison />} />


                       
                        {/* User routes */}
                        <Route
                            path="/user/*"
                            element={
                            <PrivateRoute>
                                <UserLayout />
                            </PrivateRoute>
                            }
                        >
                            <Route index element={<UserDashboard />} />
                            <Route path="furniture" element={<UserFurniture />} />
                            <Route path="orders" element={<UserOrders />} />
                            <Route path="profile" element={<UserProfile />} />
                        </Route>

                     

                        {/* Admin pages, protected */}
                        <Route
                            path="/admin/*"
                            element={
                                <PrivateRoute adminOnly={true}>
                                    <AdminLayout />
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<Dashboard />} />
                            <Route path="users" element={<UsersPage />} />
                            <Route path="users/:id" element={<AdminUserDetail />} />
                            <Route path="text-pages" element={<TextPages />} />
                            <Route path="text-pages/:id" element={<TextPageDetail />} />
                            <Route path="furniture" element={<AdminFurniture />} />
                            <Route path="carpenters" element={<CarpenterList />} />
                            <Route path="orders" element={<OrderList />} />
                            <Route path="orders/:id" element={<AdminViewOrder />} />

                            <Route path="panels" element={<AdminPanels />} />
                            <Route path="mdf" element={<AdminMdf />} />
                            <Route path="handles" element={<AdminHandles />} />
                            <Route path="legs" element={<AdminLegs />} />
                            <Route path="mirrors" element={<AdminMirrors />} />
                            <Route path="fillings" element={<AdminFillings />} />

                            <Route path="languages" element={<AdminLanguages />} />
                            <Route path="duk" element={<AdminDuk />} />
                            <Route path="discount-codes" element={<AdminDiscountCodes />} />
                            <Route path="gift-cards" element={<AdminGiftCards />} />
                            <Route path="seo" element={<AdminSeo />} />
                            <Route path="komerciniai-pasiulymai" element={<AdminOffers />} />
                            <Route path="gallery" element={<AdminGallery />} />
                            <Route path="requests" element={<AdminRequests />} />
                            <Route path="audit-log" element={<AdminAuditLog />} />
                            <Route path="statistika" element={<AdminAnalytics />} />
                            <Route path="backups" element={<AdminBackups />} />
                            <Route path="bugs" element={<AdminBugs />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="imos-surinkimas" element={<AdminImosAssembly />} />
                            <Route path="clickup" element={<AdminClickUp />} />
                            <Route path="*" element={<AdminNotFound />} />

                        </Route>

                        {/* 404 or catch-all route */}
                        <Route path="*" element={<NotFound />} />

                        {/* Dynamic route (must be last) */}
                        <Route path="/:slug" element={<Text />} />
                    </Routes>
                    </Suspense>
                    </ErrorBoundary>
                    </AppShell>
                </Router>
            </AuthProvider>
        </FluentProvider>
        </ComingSoon>
    );
}

export default App;